/**
 * Reporting (Overall > Reports), Fan 2026-10-22: download the monthly
 * financials report (P&L by business line + cashflow) for any month as a
 * PDF, and preview its headline. Same report the scheduler posts to
 * #monthly-financials on the 3rd (services/monthly_financials.py).
 * Downloads go through the authenticated client (responseType blob), never
 * a raw link: the browser would not send the bearer token.
 */
import { useEffect, useState } from 'react';
import { Loader2, Download, Eye, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { reportsAPI } from '../lib/api';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';

function num(v) {
  var n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function gbp(v) {
  var n = num(v);
  return (n < 0 ? '-' : '') + '£' + Math.abs(n).toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

function pct(part, whole) {
  var w = num(whole);
  if (!w) return '';
  return ' (' + Math.round((num(part) / w) * 100) + '%)';
}

function Row({ label, value, extra }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-border/50 last:border-0">
      <span className="text-sm">{label}</span>
      <span className={cn('text-sm tabular-nums', num(value) < 0 && 'text-red-600')}>{gbp(value)}{extra || ''}</span>
    </div>
  );
}

export function OverallReportingPage() {
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewMonth, setPreviewMonth] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(function () {
    reportsAPI.getMonthlyFinancialsMonths()
      .then(function (res) { setMonths(res.data.months || []); })
      .catch(function (err) { setError(getErrorMessage(err)); })
      .finally(function () { setLoading(false); });
  }, []);

  function openPreview(month) {
    setPreviewMonth(month);
    setPreview(null);
    setPreviewLoading(true);
    reportsAPI.getMonthlyFinancials(month)
      .then(function (res) { setPreview(res.data); })
      .catch(function (err) { toast.error(getErrorMessage(err)); })
      .finally(function () { setPreviewLoading(false); });
  }

  async function download(month) {
    setDownloading(month);
    try {
      const res = await reportsAPI.downloadMonthlyFinancialsPdf(month);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'monthly-financials-' + month + '.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () { URL.revokeObjectURL(url); }, 10000);
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Could not download the report');
    } finally {
      setDownloading(null);
    }
  }

  const g = preview && preview.group ? preview.group.month : null;
  const cf = preview ? preview.cashflow : null;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reporting</h1>
          <p className="text-muted-foreground mt-1">
            Monthly financials: profit and loss by business line, plus cashflow and why it moved. Download any month as a PDF.
          </p>
        </div>

        {loading && <div className="flex justify-center h-40 items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {error && <Card><CardContent className="pt-6 text-red-600 flex gap-2 items-center"><AlertTriangle className="h-4 w-4" />{error}</CardContent></Card>}

        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2"><CardTitle className="text-base">Monthly financials</CardTitle></CardHeader>
              <CardContent>
                {months.map(function (m) {
                  return (
                    <div key={m.month} className={cn('flex items-center justify-between gap-3 py-2 border-b border-border/50 last:border-0',
                      previewMonth === m.month && 'bg-muted/50 -mx-2 px-2 rounded')}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" />{m.label}</p>
                        <p className="text-xs text-muted-foreground">{m.posted_at ? 'Posted to Slack ' + m.posted_at.slice(0, 10) : 'Not posted to Slack'}</p>
                      </div>
                      <div className="flex gap-1.5">
                        <Button variant="ghost" size="sm" onClick={function () { openPreview(m.month); }}>
                          <Eye className="h-3.5 w-3.5 mr-1" />View
                        </Button>
                        <Button variant="outline" size="sm" onClick={function () { download(m.month); }} disabled={downloading === m.month}>
                          {downloading === m.month ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                          PDF
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {months.length === 0 && <p className="text-sm text-muted-foreground">No completed months yet.</p>}
              </CardContent>
            </Card>

            <Card className="lg:col-span-3">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{preview ? preview.month_label : 'Pick a month to see its summary'}</CardTitle>
              </CardHeader>
              <CardContent>
                {previewLoading && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" />Building the report, this can take up to a minute</div>}
                {g && (
                  <div className="space-y-5">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Overall (GBP, excl. VAT)</p>
                      <Row label="Booking Volume" value={g.revenue} />
                      <Row label="Net Revenue" value={g.net_revenue} extra={pct(g.net_revenue, g.revenue)} />
                      <Row label="Gross Margin" value={g.gross_margin} extra={pct(g.gross_margin, g.revenue)} />
                      <Row label="Net Margin" value={g.net_result} extra={pct(g.net_result, g.revenue)} />
                    </div>
                    {(preview.lines || []).map(function (l) {
                      return (
                        <div key={l.key}>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{l.label}</p>
                          <Row label="Booking Volume" value={l.month.revenue} />
                          <Row label="Net Margin" value={l.month.net_result} extra={pct(l.month.net_result, l.month.revenue)} />
                        </div>
                      );
                    })}
                    {cf && (
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Cashflow</p>
                        <Row label="Money in" value={cf.money_in} />
                        <Row label="Money out" value={cf.money_out} />
                        <Row label="Net cash flow" value={cf.net} />
                        <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                          {(cf.explanation || []).map(function (s, i) { return <li key={i}>{s}</li>; })}
                        </ul>
                      </div>
                    )}
                    <Button onClick={function () { download(preview.month); }} disabled={downloading === preview.month}>
                      {downloading === preview.month ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                      Download {preview.month_label} PDF
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
